import { ReactElement, useCallback, useEffect } from 'react'

import {
  Box,
  Button,
  Collapse,
  Grid,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { MovieAreaExpand, MovieAreaShrink } from 'src/components/icon'
import { useSendVideoCallStartedNotification } from 'src/features/explanation/api/sendVideoCallStartedNotification'
import { CameraButton, MicButton } from 'src/features/videoCall/components'
import { MediaNotEnabledPopover } from 'src/features/videoCall/components/MediaNotEnabledPopover/MediaNotEnabledPopover'
import { SPVideoCallRoomInCall } from 'src/features/videoCall/components/SPVideoCallRoomInCall/SPVideoCallRoomInCall'
import { VideoCallMenu } from 'src/features/videoCall/components/VideoCallMenu/VideoCallMenu'
import { VideoCallRoomLoading } from 'src/features/videoCall/components/VideoCallRoomLoading/VideoCallRoomLoading'
import { useLocalPlayer } from 'src/features/videoCall/context/localPlayerContext'
import { useVideoCallRoom } from 'src/features/videoCall/context/videoCallRoomContext'
import { VideoCallRoom_SpExplanationVideoCallDocument } from 'src/lib/gql-client'
import { useGqlQuerySWRNoSuspense } from 'src/lib/gql-client/request'

import { useRoomMediaContext } from '../../context/MediaProvider'
import { useInitializePatientVideoCall } from '../../hooks/useInitializePatientVideoCall'

type Props = {
  roomUid: string | undefined
  candidateId: string | undefined
  canShrinkVideoCall?: boolean
  trialPatientUID: string
}

export const SPExplanationVideoCall: React.FC<Props> = ({
  roomUid,
  candidateId,
  canShrinkVideoCall = false,
  trialPatientUID,
}) => {
  useInitializePatientVideoCall({
    roomUid,
    candidateId,
  })

  const { request: sendVideoCallStartedNotification } =
    useSendVideoCallStartedNotification()

  const sendNotification = useCallback(() => {
    sendVideoCallStartedNotification({
      trialPatientUid: trialPatientUID,
      scene: 'Explanation',
      sendTo: 'Patient',
    })
  }, [sendVideoCallStartedNotification, trialPatientUID])

  // コントロールボタンが隠れないよう、ヘッダーの高さ分だけtoastの表示位置を下げる
  useEffect(() => {
    const toastContainer = document.getElementById('chakra-toast-manager-top')
    if (!toastContainer) return
    const defaultTop = toastContainer.style.top
    toastContainer.style.top = '84px'
    return () => {
      toastContainer.style.top = defaultTop
    }
  }, [])

  return (
    <VStack>
      <SPExplanationPlayer canShrinkVideoCall={canShrinkVideoCall}>
        <SPExplanationMainContent sendNotification={sendNotification} />
      </SPExplanationPlayer>
    </VStack>
  )
}

const SPExplanationPlayer: React.FC<{
  children: ReactElement
  canShrinkVideoCall: boolean
}> = ({ children, canShrinkVideoCall }) => {
  const { localCameraEnabled, localMicrophoneEnabled } = useLocalPlayer()

  const { roomStatus } = useVideoCallRoom()

  const {
    videoExpanded,
    toggleVideoExpanded,
    showMediaNotEnabledPopover,
    setShowMediaNotEnabledPopover,
  } = useRoomMediaContext()

  useEffect(() => {
    if (localCameraEnabled || localMicrophoneEnabled) {
      setShowMediaNotEnabledPopover(false)
    }
  }, [
    localCameraEnabled,
    localMicrophoneEnabled,
    setShowMediaNotEnabledPopover,
  ])

  return (
    <VStack width="100%" spacing="0">
      <Grid
        gridTemplateColumns="1fr auto 1fr"
        columnGap="4"
        {...(!videoExpanded
          ? {
              right: 0,
              padding: 2,
              roundedLeft: 'lg',
            }
          : { width: 'full' })}
        position={videoExpanded ? 'relative' : 'absolute'}
        justifyContent={videoExpanded ? 'space-between' : 'end'}
        alignItems="center"
        h="12"
        bg="gray.900"
        px="2"
      >
        <VideoCallMenu colorScheme="green" buttonType="sp" />
        <HStack spacing="2">
          <MicButton type="sp" colorScheme="green" />
          {roomStatus === 'joining' && videoExpanded && (
            <MediaNotEnabledPopover
              isVisible={showMediaNotEnabledPopover}
              onClose={() => setShowMediaNotEnabledPopover(false)}
              placement="bottom"
              triggerHeight="28px"
            />
          )}
          <CameraButton type="sp" colorScheme="green" />
        </HStack>
        {canShrinkVideoCall && (
          <IconButton
            top={videoExpanded ? '2' : undefined}
            right={videoExpanded ? '2' : undefined}
            _focus={{ boxShadow: 'none' }}
            position={videoExpanded ? 'absolute' : undefined}
            aria-label="shrink-movie-button"
            variant="customIconButtonSolid"
            onClick={toggleVideoExpanded}
            h="8"
            w="8"
            bg="white"
            border="1px"
            borderColor="green.600"
            color="green.600"
            icon={
              videoExpanded ? (
                <MovieAreaShrink size="16px" />
              ) : (
                <MovieAreaExpand size="16px" />
              )
            }
          />
        )}
      </Grid>
      <Collapse in={videoExpanded} startingHeight="0" style={{ width: '100%' }}>
        <Box h="240px" w="100%" bg="gray.900">
          {children}
        </Box>
      </Collapse>
    </VStack>
  )
}

const SPExplanationMainContent: React.FC<{
  sendNotification: () => void
}> = ({ sendNotification }) => {
  const { roomStatus, connectionState } = useVideoCallRoom()

  if (roomStatus === 'ready') {
    return <SPVideoCallRoomReady sendNotification={sendNotification} />
  }
  if (roomStatus === 'joining') {
    return connectionState === 'CONNECTED' ? (
      <SPVideoCallRoomInCall />
    ) : (
      <VideoCallRoomLoading />
    )
  }

  return null
}

const SPVideoCallRoomReady = ({
  sendNotification,
}: {
  sendNotification: () => void
}) => {
  const { join, videoCallRoomUid, proxyStatus, agoraCloudProxyMode } =
    useVideoCallRoom()

  const { data: videoCallRoom } = useGqlQuerySWRNoSuspense(
    VideoCallRoom_SpExplanationVideoCallDocument,
    videoCallRoomUid ? { videoCallRoomUid } : null,
    {
      refreshInterval: 5000,
    },
  )

  const started = (videoCallRoom?.videoCallRoom.joinedMemberCount ?? 0) > 0

  const readyMessage = started
    ? '通話が開始されています'
    : '通話はまだ開始されていません'

  const isJoinButtonDisabled =
    agoraCloudProxyMode !== 'Auto' && proxyStatus !== 'connected'

  return (
    <VStack
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap="4"
    >
      <Text
        fontWeight="bold"
        color="white"
        px="4"
        textAlign="center"
        fontSize="xs"
      >
        {readyMessage}
      </Text>
      <Button
        colorScheme="green"
        isDisabled={isJoinButtonDisabled}
        onClick={() => {
          if (!started) sendNotification()
          join()
        }}
      >
        {started ? '今すぐ通話を開始する' : '通話を開始する'}
      </Button>
    </VStack>
  )
}
